import { useCallback, useEffect, useRef, useState } from 'react';
import { withDeadline } from '../utils/withDeadline.js';
import { renderPdfAssets } from '../utils/pdfGenerator.js';
import { base64ToBlob, buildShareMaterial, computeSealCode, createShareAttempt, getFingerprint, getOutputAsset, logShareEvent, resolveOutboundMessage, uploadOutputAsset, } from '../services/outboundShare.js';
const newIdempotencyKey = () => `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
export const useWhatsAppShare = () => {
    const [share, setShare] = useState({ stage: 'idle' });
    const attemptRef = useRef(null);
    const preparationRef = useRef(0);
    const assetContextRef = useRef(null);
    useEffect(() => () => { preparationRef.current += 1; attemptRef.current = null; assetContextRef.current?.cleanup?.(); assetContextRef.current = null; }, []);
    const logEvent = useCallback((event, meta = {}, draft = {}) => {
        if (!attemptRef.current)
            return Promise.resolve(null);
        return logShareEvent(attemptRef.current, event, meta, draft);
    }, []);
    const prepare = useCallback(async ({ docType, payload = {}, workshop = {}, templateId, templateVersion, templateSelectionReason, getElement, phone, fileBaseName, context = '', renderAssets = renderPdfAssets, validateSource, }) => {
        const preparation = ++preparationRef.current;
        assetContextRef.current?.cleanup?.();
        assetContextRef.current = null;
        setShare({ stage: 'preparing' });
        attemptRef.current = null;
        let attemptId = null;
        try {
            const payloadCustomer = payload.customer || payload.client || {};
            const payloadVehicle = payload.vehicle || {};
            const visibleDocumentNumber = payload.settings?.document_number || payload.document_number || '—';
            const resolvedPhone = phone || payloadCustomer.phone || payloadCustomer.customerPhone || '';
            const enrichedPayload = {
                ...payload,
                workshop,
                customer: { ...payloadCustomer, phone: resolvedPhone, customerPhone: resolvedPhone },
                vehicle: {
                    ...payloadVehicle,
                    plateNumber: payloadVehicle.plateNumber || payloadVehicle.plate || payload.plateNumber || '',
                    plate: payloadVehicle.plate || payloadVehicle.plateNumber || payload.plateNumber || '',
                },
                settings: {
                    ...(payload.settings || {}),
                    document_number: visibleDocumentNumber,
                    seal_code: payload.settings?.seal_code
                        || computeSealCode(visibleDocumentNumber),
                },
            };
            const material = buildShareMaterial({ docType, payload: enrichedPayload, workshop, templateId, templateVersion, templateSelectionReason });
            const resolved = await withDeadline(resolveOutboundMessage({
                doc_type: docType,
                status: material.status,
                payload: enrichedPayload,
            }), 15000, 'انتهت مهلة تحميل رسالة واتساب. تحقق من الاتصال وأعد المحاولة.');
            if (preparation !== preparationRef.current) return null;
            if (resolved.missing_variables?.length) {
                const error = new Error(`رسالة واتساب غير مكتملة. المتغيرات الناقصة: ${resolved.missing_variables.join('، ')}`);
                error.code = 'message_template_incomplete';
                error.variables = resolved.missing_variables;
                throw error;
            }
            const fp = await withDeadline(getFingerprint(material), 15000, 'انتهت مهلة التحقق من المستند. أعد المحاولة.');
            if (preparation !== preparationRef.current) return null;
            const attemptRes = await withDeadline(createShareAttempt({
                doc_type: docType,
                document_number: material.document_number,
                document_version: material.document_version,
                fingerprint: fp.fingerprint,
                action_key: resolved.template?.action_key,
                template_id: resolved.template?.id,
                template_version: resolved.template?.version,
                phone: resolvedPhone || resolved.phone?.raw || '',
                message_text: resolved.message,
                context,
                initial_event: 'message_prepared',
                idempotency_key: newIdempotencyKey(),
            }), 15000, 'تعذر تسجيل محاولة المشاركة خلال المهلة. أعد المحاولة.');
            if (preparation !== preparationRef.current) return null;
            attemptId = attemptRes.attempt?.id || null;
            attemptRef.current = attemptId;
            const attachments = resolved.template?.default_attachments ?? ['pdf', 'image'];
            assetContextRef.current = { preparation, attemptId, material, attachments, getElement, renderAssets, validateSource, busy: false };
            setShare({
                stage: 'ready',
                attemptId,
                fingerprint: fp.fingerprint,
                cached: false,
                docType,
                documentNumber: material.document_number,
                fileBaseName: fileBaseName || material.document_number || 'document',
                message: resolved.message,
                missingVariables: resolved.missing_variables || [],
                allowEdit: resolved.template?.allow_edit_before_share !== false,
                template: resolved.template,
                phone: resolved.phone,
                initialPhone: resolvedPhone || resolved.phone?.raw || '',
                customerId: payload?.customer?.id || null,
                assetStage: attachments.length ? 'idle' : 'none',
                assetError: '',
                pdfBlob: null, imageBlob: null, imageDataUrl: null,
            });
            return { message: resolved.message, phone: resolvedPhone, documentNumber: material.document_number };
        }
        catch (error) {
            const detail = error?.response?.data?.detail;
            const code = (typeof detail === 'object' ? detail?.code : null) || error?.code || 'prepare_failed';
            if (attemptId)
                void logShareEvent(attemptId, 'prepare_failed', { code: String(code) });
            if (preparation !== preparationRef.current) return null;
            setShare({
                stage: 'error',
                code,
                message: code === 'superseded_document'
                    ? 'هذا المستند مستبدل بنسخة أحدث ولا يمكن مشاركته'
                    : (typeof detail === 'object' && detail?.message) || error?.message || 'تعذر تجهيز الملفات للمشاركة — حاول مرة أخرى',
            });
        }
    }, []);
    const prepareAttachments = useCallback(async () => {
        const context = assetContextRef.current;
        if (!context || context.busy || !context.attachments.length) return;
        const isCurrent = () => assetContextRef.current === context && preparationRef.current === context.preparation;
        context.busy = true;
        setShare(previous => ({ ...previous, assetStage: 'preparing', assetError: '' }));
        let phase = 'verify';
        try {
            // A retry must not attach a replaced document to an otherwise valid-looking message.
            if (context.validateSource) await withDeadline(context.validateSource(), 15000, 'تعذر التحقق من مصدر المستند. أعد المعاينة.');
            if (!isCurrent()) return;
            const fp = await withDeadline(getFingerprint(context.material), 15000, 'تعذر إعادة التحقق من المستند. ارجع وأعد فتح الرسالة.');
            if (!isCurrent()) return;
            phase = 'asset';
            let pdfBlob = null, imageBlob = null, imageDataUrl = null, cached = false;
            if (fp.cached && fp.has_pdf) {
                const assetRes = await withDeadline(getOutputAsset(fp.fingerprint), 15000, 'انتهت مهلة تحميل المرفق. يمكنك فتح النص أو إعادة المحاولة.');
                if (!isCurrent()) return;
                const asset = assetRes.asset || {};
                if (asset.pdf_base64) pdfBlob = base64ToBlob(asset.pdf_base64, 'application/pdf');
                if (asset.preview_image_base64) {
                    imageBlob = base64ToBlob(asset.preview_image_base64, 'image/jpeg');
                    imageDataUrl = 'data:image/jpeg;base64,' + asset.preview_image_base64;
                }
                cached = true;
                void logShareEvent(context.attemptId, 'cache_hit', { fingerprint: fp.fingerprint });
            }
            if (!pdfBlob || (context.attachments.includes('image') && !imageBlob)) {
                const target = await context.getElement();
                if (!isCurrent()) { target?.cleanup?.(); return; }
                context.cleanup = target.cleanup;
                let assets;
                try {
                    assets = await withDeadline(context.renderAssets(target.element, { backgroundColor: '#ffffff', scale: 1.6 }), 45000, 'استغرق تجهيز المرفق وقتًا طويلًا. يمكنك فتح النص أو استخدام نسخة الطباعة.');
                } finally { context.cleanup?.(); context.cleanup = null; }
                if (!isCurrent()) return;
                pdfBlob = assets.pdfBlob; imageBlob = assets.imageBlob; imageDataUrl = assets.imageDataUrl;
                if (!pdfBlob || (context.attachments.includes('image') && !imageBlob)) throw new Error('لم يكتمل إنشاء المرفق. أعد المحاولة أو استخدم نسخة الطباعة.');
                cached = false;
                void logShareEvent(context.attemptId, 'pdf_generated', { size: String(pdfBlob.size) });
                if (imageBlob) void logShareEvent(context.attemptId, 'preview_image_generated', { size: String(imageBlob.size) });
                uploadOutputAsset({ material: context.material, pdf_base64: assets.pdfBase64, preview_image_base64: assets.imageBase64, idempotency_key: newIdempotencyKey() }).catch(() => null);
            }
            if (!isCurrent()) return;
            setShare(previous => ({ ...previous, assetStage: 'ready', assetError: '', cached, pdfBlob, imageBlob, imageDataUrl }));
        } catch (error) {
            if (!isCurrent()) return;
            const detail = error?.response?.data?.detail;
            const code = (typeof detail === 'object' ? detail?.code : null) || error?.code || 'attachment_failed';
            const message = (typeof detail === 'object' && detail?.message) || error?.message || 'تعذر تجهيز المرفق. يمكنك فتح رسالة واتساب بالنص فقط.';
            void logShareEvent(context.attemptId, 'prepare_failed', { code: String(code), phase });
            if (phase === 'verify' || code === 'superseded_document' || [401,403].includes(error?.response?.status)) {
                setShare({ stage: 'error', code, message: code === 'superseded_document' ? 'هذا المستند مستبدل بنسخة أحدث ولا يمكن مشاركته' : message });
                assetContextRef.current = null;
            } else {
                setShare(previous => ({ ...previous, assetStage: 'error', assetError: message, pdfBlob: null, imageBlob: null, imageDataUrl: null }));
            }
        } finally { context.busy = false; }
    }, []);
    const reset = useCallback(() => {
        preparationRef.current += 1;
        assetContextRef.current?.cleanup?.();
        assetContextRef.current = null;
        attemptRef.current = null;
        setShare({ stage: 'idle' });
    }, []);
    return { share, prepare, prepareAttachments, reset, logEvent };
};
